import React from 'react';
import clsx from 'clsx';

import { ReactComponent as ChevronIcon } from 'images/chevron.svg';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';

import {
  UpcomingItem as UpcomingItemType,
  UpcomingItemVisual,
  VisualType,
  IconName,
} from 'components/FinancialCheckup/FinancialDashboard/UpcomingSection/useUpcomingData';
import { ReactComponent as DoneIcon } from 'images/done-icon.svg';
import { ReactComponent as TransferIcon } from 'images/transfer-icon.svg';
import { ReactComponent as SettingsIcon } from 'images/settings-icon.svg';
import { ReactComponent as AssignmentIcon } from 'images/assignment-icon.svg';

import styles from './UpcomingItem.module.scss';

interface UpcomingItemProps {
  item: UpcomingItemType;
}

const iconComponentMap: Record<IconName, JSX.Element> = {
  [IconName.Done]: <DoneIcon className={clsx(styles.icon, styles.fillBlue)} />,
  [IconName.Pending]: <DoneIcon className={clsx(styles.icon, styles.pendingIcon)} />,
  [IconName.Transfer]: <TransferIcon className={clsx(styles.icon, styles.fillBlue)} />,
  [IconName.Settings]: <SettingsIcon className={clsx(styles.icon)} />,
  [IconName.Assignment]: <AssignmentIcon className={clsx(styles.icon)} />,
};

const renderVisual = (visual?: UpcomingItemVisual) => {
  if (!visual) return null;

  switch (visual.type) {
    case VisualType.Emoji:
      return <span className={styles.emoji}>{visual.value}</span>;
    case VisualType.Logo:
      return <img src={visual.url} alt="" className={styles.logo} />;
    case VisualType.Icon:
      return iconComponentMap[visual.name];
    default:
      return null;
  }
};

const UpcomingItem: React.FC<UpcomingItemProps> = ({ item }) => {
  const { title, description, value, onClick, visual } = item;
  const isClickable = !!onClick;

  return (
    <div className={clsx(styles.item, isClickable && styles.clickable)} onClick={onClick}>
      {visual && <div className={styles.iconContainer}>{renderVisual(visual)}</div>}
      <div className={styles.content}>
        <p className={styles.title}>{title}</p>
        {description && <p className={styles.description}>{description}</p>}
      </div>
      {value !== undefined && (
        <div className={styles.valueContainer}>
          <p className={styles.value}>{typeof value === 'number' ? formatMonetaryAmount(value, true) : value}</p>
        </div>
      )}
      {isClickable && (
        <div className={styles.chevronContainer}>
          <ChevronIcon className={styles.chevron} />
        </div>
      )}
    </div>
  );
};

export default UpcomingItem;
